/**
 * @file right-type-labels.ts
 * @description RightType enum을 한글 라벨로 변환하는 유틸리티
 *
 * 주요 기능:
 * 1. RightType enum 값을 한글 라벨로 변환
 * 2. 모든 RightType enum을 커버하는 타입 안전한 매핑
 * 3. SSOT 원칙: 모든 한글 라벨은 이 파일에서만 관리
 *
 * 사용 예시:
 * ```ts
 * import { getRightTypeLabel } from "@/lib/domain/auction/utils/right-type-labels";
 * const label = getRightTypeLabel(RightType.Mortgage); // "근저당권"
 * ```
 *
 * @dependencies
 * - @/lib/types: RightType enum
 *
 * @see {@link /docs/domain/rights-types.md} - RightType 명세
 */

import { RightType } from "@/lib/core/types";

/**
 * RightType enum을 한글 라벨로 매핑하는 객체
 * 모든 RightType enum을 커버해야 함 (타입 안전성 보장)
 */
export const RIGHT_TYPE_LABELS: Record<RightType, string> = {
  [RightType.Mortgage]: "근저당권",
  [RightType.Pledge]: "질권",
  [RightType.ProvisionalSeizure]: "가압류",
  [RightType.Seizure]: "압류",
  [RightType.Injunction]: "가처분",
  [RightType.Leasehold]: "임차권",
  [RightType.TenantProtected]: "보호 임차인",
  [RightType.TenantUnprotected]: "일반 임차인",
  [RightType.Lien]: "유치권",
  [RightType.StatutorySurface]: "법정지상권",
  [RightType.SurfaceRight]: "지상권",
  [RightType.Easement]: "지역권",
  [RightType.GraveRight]: "분묘기지권",
  [RightType.PriorLeasehold]: "선순위 임차권",
  [RightType.PriorTenant]: "선순위 임차인",
  [RightType.TaxLien]: "조세 체납",
  [RightType.NoticeRegistry]: "예고등기",
  [RightType.ProvisionalRegistry]: "가등기",
};

/**
 * RightType enum 값을 한글 라벨로 변환하는 함수
 *
 * @param type - RightType enum 값
 * @returns 한글 라벨 (매핑이 없으면 enum 값 그대로 반환)
 *
 * @example
 * ```ts
 * getRightTypeLabel(RightType.Mortgage) // "근저당권"
 * getRightTypeLabel(RightType.ProvisionalSeizure) // "가압류"
 * ```
 */
export function getRightTypeLabel(type: RightType): string {
  return RIGHT_TYPE_LABELS[type] || type;
}
